import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';

import Layout from '../../../Layout/Layout';
import { Button } from '../../../../components/ui/Button';
import AddContentPopup from '../../../SharedComponents/AddContentPopup/AddContentPopup';
import DragDrop from '../../../SharedComponents/DragDrop/DragDrop';
import PlaylistItemSettingsSheet from '../../../SharedComponents/DragDrop/PlaylistItemSettingsSheet';
import { MediaPreviewProvider, useMediaPreview, isPreviewable } from '../../../SharedComponents/MediaPreview/MediaPreviewContext';
import FirebaseMediaListener from '../../../Services/FirebaseMediaListener';
import { useCustomToast } from '../../../Contexts/ToastContext';
import '../../../Campaign/css/setup.css';

import {
  useCreateTagMutation,
  useDetachTag,
  useGetCompanyTags,
  useGetPlaylist,
  useGetPlaylistLayerContent,
  usePublishPlaylistMutation,
  useResetPlaylistMutation,
  useRetrievePlaylistContentDuration,
  useUpdatePlaylistMutation,
} from '../../../../queries';
import { usePlaylistPublishImpact } from '../../../../queries/showPage';
import { useTitle, formatDurationCompact } from '../../../../config/global';
import { toggleDialog, setLayer, setMediaIdReset } from '../../../../redux/Slices/Global/globalSlice';
import { setPlaylistDetails, setPlaylistFetchingLoading } from '../../../../redux/Slices/playlistSlice';

// Same "Auto-created" convention the campaign builder side already
// identifies by (CampaignVibesSection.jsx / MediaList.jsx's own
// AUTO_TAG_NAME) — display-only Arabic label mapping, the stored tag name
// in the database stays "Auto-created".
const AUTO_TAG_NAME = 'Auto-created';

// config/global.js's `formatPublishedDate` hardcodes Intl.DateTimeFormat
// ("en-US", ...) — shared by CampaignSetup/InteractiveCampaignSetup/
// LayoutSetup too, so fixing it there would silently change those three
// other pages' output, well beyond this page's scope. Same local-override
// pattern ShowPage.jsx already uses for the identical gap
// (`formatPublishedDateLocalized`, ShowPage.jsx:113-129) — a
// ShowPage/PlaylistSetup-local, locale-aware formatter rather than a shared
// fix. No existing relative-time ("قبل ساعتين") helper was found elsewhere
// in this codebase (checked for dayjs/date-fns relative formatting and any
// custom "قبل" helper), so this mirrors ShowPage.jsx's choice of a full
// locale-formatted date/time over inventing a new relative-time scheme.
function formatPublishedDateLocalized(dateString, lng) {
  if (!dateString) return '';
  try {
    const raw = String(dateString);
    const date = raw.includes('T') ? new Date(raw) : new Date(`${raw.replace(' ', 'T')}Z`);
    if (isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat(lng === 'ar' ? 'ar-EG' : 'en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  } catch (e) {
    return dateString;
  }
}

// v15 media preview — provider mounted at this page root (see
// CampaignContentSetup.jsx's identical wrapper and MediaPreviewContext.jsx's
// doc comment for why: one shared audio engine/lightbox per page, torn down
// on route change along with the page itself).
function PlaylistSetup() {
  return (
    <MediaPreviewProvider>
      <PlaylistSetupBody />
    </MediaPreviewProvider>
  );
}

function PlaylistSetupBody() {
  useTitle('Playlist Setup');
  const { t, i18n } = useTranslation();
  const { showToast } = useCustomToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();

  useGetCompanyTags();
  const { layer, companyTags, openDialog, mediaIdReset } = useSelector((state) => state.global);
  const { playlistDetails, playlistFetchingLoading } = useSelector((state) => state.playlist);

  const { isLoading, data, isError, refetch: refetchLayerData } = useGetPlaylistLayerContent(id, layer);
  const { playlistLoading, playlistInfo } = useGetPlaylist(id);
  const { data: impact } = usePlaylistPublishImpact(id);
  const campaignCount = impact?.campaign_count ?? 0;

  const publishPlaylist = usePublishPlaylistMutation();
  const resetPlaylist = useResetPlaylistMutation();
  const updatePlaylist = useUpdatePlaylistMutation();
  const detachTag = useDetachTag();
  const { mutateAsync: createTag, isLoading: createTagLoading } = useCreateTagMutation();

  const isPublishing = publishPlaylist.isLoading;
  const playlistPublishResetLoading = publishPlaylist.isLoading || resetPlaylist.isLoading || playlistFetchingLoading;

  // Same redux bookkeeping the pre-redesign page relied on: several
  // playlist-content mutations (useOrderPlaylistContent,
  // useUpdatePlaylistContentSettings, useRetrievePlaylistContentDuration,
  // useDeletePlaylistContent, useDuplicatePlaylistContent — all in
  // queries/playlistContent.js) read `state.playlist.playlistDetails.id` and
  // `state.global.layer` to know which query keys to invalidate. Dropping
  // this would silently break reorder/duplicate/delete/duration refresh.
  useEffect(() => {
    dispatch(setLayer('MAIN'));
    return () => {
      dispatch(setPlaylistDetails({}));
      dispatch(setMediaIdReset([]));
    };
  }, [dispatch]);

  // Mirrors CampaignSetup.jsx's own `pp-campaign-setup-active` body class
  // (CampaignSetup.jsx:356-357): scopes the playlist-page-only CSS overrides
  // below (inline rename pencil, hiding Navbar's leftover mobile sub-bar)
  // without touching the shared `.pp-cb-*` rules the campaign builder also
  // uses.
  useEffect(() => {
    document.body.classList.add('pp-playlist-setup-active');
    return () => document.body.classList.remove('pp-playlist-setup-active');
  }, []);

  useEffect(() => {
    dispatch(setPlaylistDetails(playlistInfo));
  }, [playlistInfo, dispatch]);

  useEffect(() => {
    if (playlistInfo?.content_layer) {
      dispatch(setLayer(playlistInfo.content_layer));
    }
  }, [playlistInfo?.content_layer, dispatch]);

  const items = Array.isArray(data) ? data : [];

  const [itemSheet, setItemSheet] = useState(null);

  // v15 media preview — same pattern as CampaignContentSetup.jsx's own
  // openRowPreview: DragDrop.jsx's onRowPreview calls this with (item, list),
  // scoping the lightbox's nav to this playlist's own visual siblings and
  // stopping audio on close (row-preview semantics, not the sheet-grid's).
  const mediaPreview = useMediaPreview();
  const openRowPreview = (item, list) => {
    const visual = (list || []).filter(isPreviewable);
    const idx = visual.indexOf(item);
    mediaPreview.openLightbox(visual, idx < 0 ? 0 : idx, { stopAudioOnClose: true });
  };

  // Carried over from the pre-redesign page: after an upload-triggered
  // Firebase media-processing push, retrieve each newly-added media id's
  // REAL duration (useRetrieveNewMediaDuration's playlist counterpart) so a
  // freshly uploaded item doesn't sit at a placeholder/zero duration until
  // some unrelated refetch happens to fire.
  const retrieveDuration = useRetrievePlaylistContentDuration();
  const mediaIdResetRef = useRef(mediaIdReset);
  useEffect(() => { mediaIdResetRef.current = mediaIdReset; }, [mediaIdReset]);
  const galleryRefetch = useCallback(async () => {
    const currentMediaIds = mediaIdResetRef?.current;
    if (currentMediaIds?.length > 0) {
      for (const mediaId of currentMediaIds) {
        await retrieveDuration.mutateAsync(mediaId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── header: rename ──────────────────────────────────────────────
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const nameInputRef = useRef(null);

  const startRename = () => {
    setNameDraft(playlistDetails?.name || '');
    setEditingName(true);
  };

  // ⚠️ PUT /playlist/{id} validates `status` as `required` (PlaylistRequest
  // ::rules()) and PlaylistController::update()/PlaylistRepository just
  // fill()+save() name+status verbatim — a name-only payload would 422, and
  // there is no safe way to omit status. Echoing back the CURRENT status
  // value alongside the new name is a true no-op on that column (confirmed
  // by reading PlaylistRepository::validate()/store() — no other side
  // effect fires from this update() path), so rename is safe done this way
  // without inventing a new endpoint.
  const commitRename = async () => {
    setEditingName(false);
    const trimmed = nameDraft.trim();
    if (!trimmed || trimmed === playlistDetails?.name) return;
    await updatePlaylist.mutateAsync(
      { id, data: { name: trimmed, status: playlistInfo?.status } },
      { onSuccess: () => { queryClient.invalidateQueries(['getPlaylist', id]); showToast('success', t('playlist.rename_tooltip'), t('playlist.rename_saved_toast')); } },
    );
  };

  // ─── header: publish / discard ──────────────────────────────────
  const neverPublished = playlistDetails?.neverPublished === true;
  const hasUnpublishedChanges = playlistDetails?.has_unpublished_changes === true;
  const canPublish = hasUnpublishedChanges && !(neverPublished && items.length === 0) && !isLoading;

  const publish = async () => {
    dispatch(setPlaylistFetchingLoading(true));
    await publishPlaylist.mutateAsync({ id, name: playlistInfo?.name }, {
      onSuccess: () => showToast('success', t('buttons.publish'), t('playlist.update_toast_success')),
    });
  };

  const reset = async () => {
    await resetPlaylist.mutateAsync({ id, name: playlistInfo?.name }, {
      onSuccess: () => showToast('success', t('playlist.discard_changes'), t('playlist.update_toast_success')),
    });
  };

  const noticeText = neverPublished
    ? (items.length ? t('playlist.notice_never_items') : t('playlist.notice_never_empty'))
    : hasUnpublishedChanges
      ? (campaignCount > 1 ? t('playlist.notice_dirty_shared', { count: campaignCount }) : t('playlist.notice_dirty'))
      : null;
  const showDiscard = !neverPublished && hasUnpublishedChanges;

  // ─── tags (saved immediately, independent of publish) ───────────
  const [tagQuery, setTagQuery] = useState('');
  const [tagOpen, setTagOpen] = useState(false);
  const [tagHighlight, setTagHighlight] = useState(-1);
  const tagInputRef = useRef(null);

  const tagsList = Array.isArray(playlistDetails?.tags_list) ? playlistDetails.tags_list : [];

  const tagSuggestions = useMemo(() => {
    const q = tagQuery.trim();
    const attached = new Set(tagsList.map((t2) => t2.name));
    const base = (companyTags || [])
      .filter((ct) => ct.name.toLowerCase().includes(q.toLowerCase()) && !attached.has(ct.name))
      .slice(0, 6)
      .map((ct) => ({ id: ct.id, name: ct.name, isNew: false }));
    if (q && !(companyTags || []).some((ct) => ct.name === q) && !attached.has(q)) {
      base.push({ name: q, isNew: true });
    }
    return base;
  }, [tagQuery, companyTags, tagsList]);

  const deleteTag = async (tagId, tagName) => {
    await detachTag.mutateAsync({ tag_id: tagId, tagged_id: id }, {
      onSuccess: () => showToast('success', t('playlist.tags_section_title'), t('playlist.tag_removed_toast', { name: tagName })),
    });
  };

  const addTag = async (rawName) => {
    const name = rawName.trim();
    if (!name) return;
    if (tagsList.some((t2) => t2.name === name)) {
      showToast('error', t('playlist.tags_section_title'), t('playlist.tag_duplicate_error'));
      return;
    }
    const tagName = name.replace(/\s+/g, '-');
    await createTag({ name: tagName, tagged_id: id, type: 'playlist' }, {
      onSuccess: () => showToast('success', t('playlist.tags_section_title'), t('playlist.tag_added_toast', { name: tagName })),
    });
    setTagQuery('');
    setTagOpen(false);
    setTagHighlight(-1);
    tagInputRef.current?.focus();
  };

  const onTagKeyDown = (e) => {
    if (e.key === 'ArrowDown') { setTagHighlight((h) => Math.min(h + 1, tagSuggestions.length - 1)); e.preventDefault(); }
    else if (e.key === 'ArrowUp') { setTagHighlight((h) => Math.max(h - 1, -1)); e.preventDefault(); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      const pick = tagHighlight >= 0 ? tagSuggestions[tagHighlight] : null;
      addTag(pick ? pick.name : tagQuery);
    } else if (e.key === 'Escape') { setTagOpen(false); }
  };

  // ─── meta chips ──────────────────────────────────────────────────
  // ⚠️ content_layer is NOT the binary MAIN/PLAYGROUND the prototype assumed
  // — live-queried against dev (100 real playlists): MAIN, PLAYGROUND, AND
  // also CORNER (4) and BANNER (1), both real, both playlist_media's own
  // enum values (see create_playlist_media_table migration). Labeled
  // explicitly below rather than folding CORNER/BANNER into "ambient",
  // which would misdescribe them.
  const resolvedLayer = playlistInfo?.content_layer || layer;
  const isMain = resolvedLayer === 'MAIN';
  const LAYER_LABEL_KEY = {
    MAIN: 'playlist.mchip_layer_main',
    PLAYGROUND: 'playlist.mchip_layer_ambient',
    CORNER: 'playlist.mchip_layer_corner',
    BANNER: 'playlist.mchip_layer_banner',
  };
  const layerLabel = t(LAYER_LABEL_KEY[resolvedLayer] || 'playlist.mchip_layer_ambient');
  const totalDuration = playlistDetails?.playlist_duration;

  const usageChip = campaignCount > 1
    ? { cls: 'info', icon: 'pi pi-clone', label: t('playlist.mchip_usage_many', { count: campaignCount }) }
    : campaignCount === 1
      ? { cls: '', icon: 'pi pi-clone', label: t('playlist.mchip_usage_one') }
      : { cls: '', icon: 'pi pi-clone', label: t('playlist.mchip_usage_none') };

  // ─── empty / add-content ─────────────────────────────────────────
  const openAddContent = () => dispatch(toggleDialog('addContent'));

  return (
    <>
      <Layout padding={false} subNavbarProps={{}} subNavbarContent={null}>
        <div className="pp-campaign-setup-host">
          <div className="pp-cb-appbar">
            <div className="pp-cb-wrap">
              <div className="pp-cb-bar-row">
                <button className="pp-cb-iconbtn pp-cb-back" aria-label={t('buttons.back')} onClick={() => navigate('/playlist/list')}>
                  <i className="pi pi-arrow-right" style={{ fontSize: 19 }}></i>
                </button>

                <div className="pp-cb-htitle">
                  {playlistLoading ? (
                    <h1>{t('messages.loading')}</h1>
                  ) : editingName ? (
                    <input
                      ref={nameInputRef}
                      className="pp-cb-name-in"
                      autoFocus
                      maxLength={50}
                      value={nameDraft}
                      onChange={(e) => setNameDraft(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') e.currentTarget.blur();
                        if (e.key === 'Escape') { setNameDraft(playlistDetails?.name || ''); setEditingName(false); }
                      }}
                    />
                  ) : (
                    <h1>
                      <span className="nm">{playlistDetails?.name}</span>
                      <button type="button" className="pp-cb-rename" title={t('playlist.rename_tooltip')} aria-label={t('playlist.rename_tooltip')} onClick={startRename}>
                        <i className="fas fa-pencil" style={{ fontSize: 13 }} />
                      </button>
                    </h1>
                  )}
                  <div className="pp-cb-sub">
                    <span className={`pp-cb-status-chip ${neverPublished ? 'never' : 'live'}`}>
                      <span className="d"></span>
                      {neverPublished ? t('playlist.status_never_chip') : t('playlist.status_live_chip')}
                    </span>
                    <span className="pp-cb-dotsep"></span>
                    <span className="pp-cb-nowrap-last">
                      {neverPublished ? t('playlist.never_published') : t('playlist.last_published', { date: playlistDetails?.last_published ? formatPublishedDateLocalized(playlistDetails?.last_published, i18n.language) : t('playlist.unknown_date') })}
                    </span>
                  </div>
                </div>

                <div className="pp-cb-toolbar">
                  <div className="pp-cb-desk">
                    <Button
                      className="p-button-outlined"
                      label={t('playlist.add_content')}
                      icon={<i className="pi pi-plus" />}
                      onClick={openAddContent}
                      disabled={playlistLoading || isLoading}
                    />
                  </div>
                  <Button
                    id="playlist-publish-btn"
                    label={t('buttons.publish')}
                    icon={<i className={isPublishing ? 'pi pi-spin pi-spinner' : 'pi pi-check'} />}
                    disabled={!canPublish}
                    onClick={publish}
                  />
                </div>
              </div>

              {noticeText && (
                <div className="pp-cb-notice">
                  <i className="pi pi-info-circle"></i>
                  <span className="pp-cb-notice-grow">{noticeText}</span>
                  {showDiscard && (
                    <button className="pp-cb-notice-discard" onClick={reset}>{t('playlist.discard_changes')}</button>
                  )}
                </div>
              )}

              {playlistPublishResetLoading && (
                <div className="pp-cb-notice">
                  <i className="pi pi-spin pi-spinner"></i>
                  <span className="pp-cb-notice-grow">{t('playlist.saving')}</span>
                </div>
              )}

              {!playlistLoading && !isLoading && (
                <div className="pp-cb-mchips">
                  <span className="pp-cb-mchip">
                    <i className="pi pi-sitemap" />
                    {layerLabel}
                  </span>
                  <span className="pp-cb-mchip">
                    <i className="pi pi-list" />
                    <b>{items.length}</b>&nbsp;{t('playlist.mchip_items_suffix')}
                  </span>
                  {isMain && items.length > 0 && (
                    <span className="pp-cb-mchip">
                      <i className="pi pi-clock" />
                      {t('playlist.mchip_total_duration')}&nbsp;<b>{formatDurationCompact(totalDuration)}</b>
                    </span>
                  )}
                  <span className={`pp-cb-mchip ${usageChip.cls}`}>
                    <i className={usageChip.icon} />
                    {usageChip.label}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="pp-campaign-setup-scroll">
            <div className="pp-cb-wrap">
              <section className="pp-cb-section" style={{ marginTop: 6 }}>
                <div className="pp-cb-sec-head">
                  <div className="pp-cb-txt">
                    <h2>{t('playlist.tags_section_title')}</h2>
                    <div className="pp-cb-desc">{t('playlist.tags_section_desc')}</div>
                  </div>
                </div>
                <div className="pp-cb-tagcard">
                  <div className="pp-cb-tagwrap">
                    {tagsList.map((tg) => {
                      const isAuto = tg.name === AUTO_TAG_NAME;
                      return (
                        <span key={tg.id} className={`pp-cb-tag-chip ${isAuto ? 'auto' : ''}`} title={isAuto ? t('playlist.auto_tag_tooltip') : undefined}>
                          {isAuto ? t('playlist.auto_tag_label') : tg.name}
                          <button type="button" className="x" aria-label={t('buttons.delete')} onClick={() => deleteTag(tg.id, tg.name)} disabled={detachTag.isLoading}>
                            <i className="pi pi-times" style={{ fontSize: 10 }} />
                          </button>
                        </span>
                      );
                    })}
                    <div className="pp-cb-tag-in-wrap">
                      <input
                        ref={tagInputRef}
                        className="pp-cb-tag-in"
                        placeholder={t('playlist.tag_input_placeholder')}
                        maxLength={30}
                        autoComplete="off"
                        value={tagQuery}
                        onFocus={() => setTagOpen(true)}
                        onChange={(e) => { setTagQuery(e.target.value); setTagHighlight(-1); setTagOpen(true); }}
                        onBlur={() => setTimeout(() => setTagOpen(false), 120)}
                        onKeyDown={onTagKeyDown}
                      />
                      {tagOpen && tagSuggestions.length > 0 && (
                        <div className="pp-cb-sugg">
                          {tagSuggestions.map((s, i) => (
                            <button
                              key={s.isNew ? `new-${s.name}` : s.id}
                              type="button"
                              className={`${s.isNew ? 'new' : ''} ${i === tagHighlight ? 'hl' : ''}`}
                              onMouseDown={(e) => { e.preventDefault(); addTag(s.name); }}
                            >
                              <i className={s.isNew ? 'pi pi-plus' : 'pi pi-tag'} />
                              {s.isNew ? t('playlist.tag_create_option', { name: s.name }) : s.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {createTagLoading && <i className="pi pi-spin pi-spinner" />}
                  </div>
                </div>
              </section>

              <section className="pp-cb-section">
                <div className="pp-cb-sec-head">
                  <div className="pp-cb-txt">
                    <h2>{t('playlist.content_section_title')}</h2>
                    <div className="pp-cb-desc">{t('playlist.content_section_desc')}</div>
                  </div>
                </div>

                {isLoading || playlistLoading ? (
                  <div className="pp-cb-list">
                    {[0, 1, 2].map((i) => (
                      <div className="pp-cb-sk" key={i}>
                        <div className="b" style={{ width: 46, height: 46, borderRadius: 11 }} />
                        <div style={{ flex: 1 }}>
                          <div className="b" style={{ height: 13, width: '55%' }} />
                          <div className="b" style={{ height: 11, width: '32%', marginTop: 8 }} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : isError ? (
                  <div className="pp-blank pp-cb-blank pp-blank--err">
                    <div className="pp-blank-icon"><i className="pi pi-exclamation-circle" /></div>
                    <h3>{t('playlist.error_title')}</h3>
                    <p>{t('playlist.error_desc')}</p>
                    <div className="pp-blank-actions">
                      <Button label={t('buttons.retry')} icon={<i className="pi pi-refresh" />} onClick={() => refetchLayerData()} />
                    </div>
                  </div>
                ) : items.length === 0 ? (
                  <div className="pp-blank">
                    <div className="pp-blank-icon"><i className="bx bxs-playlist" /></div>
                    <h3>{t('playlist.empty_state_title')}</h3>
                    <p>{isMain ? t('playlist.empty_state_desc_main') : t('playlist.empty_state_desc_ambient')}</p>
                    <div className="pp-blank-actions">
                      <Button label={t('playlist.add_content')} icon={<i className="pi pi-plus" />} onClick={openAddContent} />
                    </div>
                  </div>
                ) : (
                  <>
                    <DragDrop
                      data={items}
                      builderRow
                      playlistId={id}
                      playlistLayer={layer}
                      onPreview={openRowPreview}
                      onOpenItemSettings={(item) => setItemSheet(item)}
                    />
                    <button className="pp-cb-additem" onClick={openAddContent}>
                      <i className="pi pi-plus" />
                      {t('playlist.add_content')}
                    </button>
                  </>
                )}
              </section>
            </div>
          </div>
        </div>
      </Layout>

      {openDialog.addContent ? (
        <AddContentPopup
          playlistSheetMode
          titleOverride={isMain ? t('addcontent.title_main') : t('addcontent.title_vibes')}
          subtitleOverride={isMain ? t('addcontent.subtitle_main') : t('addcontent.subtitle_vibes')}
        />
      ) : null}

      {itemSheet && (
        <PlaylistItemSettingsSheet item={itemSheet} layer={layer} playlistId={id} onClose={() => setItemSheet(null)} />
      )}

      <FirebaseMediaListener
        path={'/Companies/' + localStorage.getItem('company_id') + '/actions'}
        getData={galleryRefetch}
      />
    </>
  );
}

export default PlaylistSetup;
